import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText, CheckCircle, Clock, PenTool, Loader2,
  Building, PoundSterling, User, Search, Eye
} from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import { API } from "@/App";

export default function ContractsManagementPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [pendingContracts, setPendingContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signing, setSigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  
  const sigCanvas = useRef(null);

  useEffect(() => {
    fetchContracts();
    fetchPendingContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(response.data.contracts || []);
    } catch (error) {
      console.error("Error fetching contracts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts/pending/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingContracts(response.data.contracts || []);
    } catch (error) {
      console.error("Error fetching pending contracts:", error);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleCountersign = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      toast.error("Please provide your signature");
      return;
    }
    
    setSigning(true);
    try {
      const signatureData = sigCanvas.current.toDataURL("image/png");
      
      await axios.post(`${API}/contracts/${selectedContract.contract_id}/countersign`, {
        signature_data: signatureData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Contract countersigned successfully!");
      setShowSignDialog(false);
      setSelectedContract(null);
      fetchContracts();
      fetchPendingContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to countersign contract");
    } finally {
      setSigning(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending_seller_signature":
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Awaiting Seller</Badge>;
      case "pending_staff_signature":
        return <Badge className="bg-blue-100 text-blue-700"><PenTool className="w-3 h-3 mr-1" /> Ready to Sign</Badge>;
      case "completed":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Completed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const filteredContracts = contracts.filter(c => 
    c.property_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.seller_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Contracts</h1>
          <p className="text-slate-500">Manage seller property instruction contracts</p>
        </div>
        {pendingContracts.length > 0 && (
          <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1">
            {pendingContracts.length} awaiting your signature
          </Badge>
        )}
      </div>

      {/* Pending Countersignatures Alert */}
      {pendingContracts.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-blue-900 text-lg flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Contracts Ready for Your Signature
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingContracts.map((contract) => (
                <div 
                  key={contract.contract_id}
                  className="flex items-center justify-between bg-white p-4 rounded-lg border"
                >
                  <div>
                    <p className="font-medium">{contract.property_address}</p>
                    <p className="text-sm text-slate-500">
                      {contract.seller_name} • Signed {new Date(contract.seller_signed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button 
                    onClick={() => {
                      setSelectedContract(contract);
                      setShowSignDialog(true);
                    }}
                    data-testid={`countersign-btn-${contract.contract_id}`}
                  >
                    <PenTool className="w-4 h-4 mr-2" />
                    Countersign
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="all">All Contracts</TabsTrigger>
        </TabsList>

        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search by property or seller..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <TabsContent value="pending" className="mt-4">
          <ContractsList 
            contracts={filteredContracts.filter(c => c.status !== "completed")}
            onView={(c) => setSelectedContract(c)}
            onSign={(c) => {
              setSelectedContract(c);
              setShowSignDialog(true);
            }}
            getStatusBadge={getStatusBadge}
          />
        </TabsContent>

        <TabsContent value="completed" className="mt-4">
          <ContractsList 
            contracts={filteredContracts.filter(c => c.status === "completed")}
            onView={(c) => setSelectedContract(c)}
            getStatusBadge={getStatusBadge}
          />
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <ContractsList 
            contracts={filteredContracts}
            onView={(c) => setSelectedContract(c)}
            onSign={(c) => {
              setSelectedContract(c);
              setShowSignDialog(true);
            }}
            getStatusBadge={getStatusBadge}
          />
        </TabsContent>
      </Tabs>

      {/* Countersign Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Countersign Contract</DialogTitle>
          </DialogHeader>
          
          {selectedContract && (
            <div className="space-y-4">
              {/* Contract Summary */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Property</span>
                  <span className="font-medium">{selectedContract.property_address}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Seller</span>
                  <span className="font-medium">{selectedContract.seller_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Asking Price</span>
                  <span className="font-medium">£{selectedContract.asking_price?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Commission</span>
                  <span className="font-medium">£{selectedContract.total_fee?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Seller Signed</span>
                  <span className="font-medium">{new Date(selectedContract.seller_signed_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Signature Pad */}
              <div>
                <Label className="mb-2 block">Your Signature</Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      width: 550,
                      height: 150,
                      className: "signature-canvas w-full"
                    }}
                    backgroundColor="white"
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={clearSignature} className="mt-2">
                  Clear Signature
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCountersign} disabled={signing}>
              {signing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete & Sign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Contracts List Component
function ContractsList({ contracts, onView, onSign, getStatusBadge }) {
  if (contracts.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-500">No contracts found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {contracts.map((contract) => (
        <Card key={contract.contract_id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="font-semibold">{contract.property_address}</h3>
                  {getStatusBadge(contract.status)}
                </div>
                <div className="flex gap-6 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {contract.seller_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <PoundSterling className="w-4 h-4" />
                    £{contract.asking_price?.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    £{contract.total_fee?.toLocaleString()} fee
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => onView(contract)}>
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                {contract.status === "pending_staff_signature" && onSign && (
                  <Button size="sm" onClick={() => onSign(contract)}>
                    <PenTool className="w-4 h-4 mr-1" />
                    Sign
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
